import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Lock, Building2, Globe, Rocket, Shield,
  Eye, EyeOff, RefreshCw, Copy, Check, Upload,
  ArrowRight, ArrowLeft, Loader2, Sparkles, Zap, AlertCircle,
  FileKey, Network, Mail, User, MapPin, Image,
  X, HelpCircle, Play, Target, Languages, Users,
  ShieldCheck, Key, Smartphone, Info, BookOpen, Search,
  FileSignature, Phone, Send, Eraser, ScrollText,
  Maximize2, Minimize2, RotateCcw, Clock, Timer
} from 'lucide-react';
import apiClient from '../../api/client';
import { useI18n } from '../../i18n';
import './SetupWizard.css';

// Language options
const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' }
];

// Translations for setup wizard
const TRANSLATIONS = {
  fr: {
    // Step headers
    step1Title: 'Langue de l\'interface',
    step1Desc: 'Sélectionnez la langue qui sera utilisée dans toute l\'application et vos rapports de pentest.',
    step2Title: 'E-mail vérifié avec succès',
    step2Desc: 'Votre adresse e-mail a été confirmée. Vous pouvez maintenant configurer votre espace de travail sécurisé.',
    step3Title: 'Sécurisez votre compte',
    step3Desc: 'Créez un mot de passe robuste pour protéger vos données de pentest sensibles. Nous recommandons d\'activer la 2FA.',
    step4Title: 'Informations de votre entreprise',
    step4Desc: 'Ces informations apparaîtront sur vos rapports de pentest et permettent de personnaliser votre espace.',
    step5Title: 'Signature du contrat',
    step5Desc: 'Lisez et acceptez les conditions d\'utilisation de la plateforme. Une vérification par SMS et votre signature électronique sont requises.',
    step6Title: 'Configuration réseau interne',
    step6Desc: 'Configurez un VPN pour scanner votre infrastructure interne en toute sécurité. Cette étape est optionnelle.',
    step7Title: 'Votre espace est prêt !',
    step7Desc: 'Félicitations ! Votre plateforme de pentest IA est configurée. Lancez-la pour commencer vos analyses de sécurité.',
    // Contract step translations
    contractTitle: 'Contrat d\'utilisation',
    contractSubtitle: 'Veuillez lire attentivement les conditions générales d\'utilisation',
    scrollToAccept: 'Faites défiler jusqu\'en bas pour accepter',
    phoneVerification: 'Vérification par SMS',
    enterPhone: 'Numéro de téléphone',
    sendCode: 'Envoyer le code',
    codeSent: 'Code envoyé !',
    enterCode: 'Code de vérification',
    verifyCode: 'Vérifier',
    codeVerified: 'Numéro vérifié',
    invalidCode: 'Code invalide',
    signatureTitle: 'Signature électronique',
    signatureDesc: 'Signez dans le cadre ci-dessous avec votre souris ou votre doigt',
    clearSignature: 'Effacer',
    acceptContract: 'J\'accepte les conditions générales d\'utilisation, les CGV et CGU',
    bothRequired: 'La vérification SMS et la signature sont requises',
    contractAccepted: 'Contrat accepté',
    // SMS Timer
    resendIn: 'Renvoyer dans',
    resendCode: 'Renvoyer le code',
    codeValidFor: 'Code valide pendant 10 minutes',
    // Email fallback
    smsNotReceived: 'SMS non reçu ?',
    sendByEmail: 'Recevoir le code par e-mail',
    sendingEmail: 'Envoi en cours...',
    codeSentByEmail: 'Code envoyé par e-mail',
    emailSentTo: 'E-mail envoyé à',
    // Contract fullscreen
    viewFullscreen: 'Plein écran',
    closeFullscreen: 'Fermer',
    // Signature
    signHere: 'Signez ici',
    signatureGuide: 'Ligne de signature',
    // Sidebar labels
    sidebarLanguage: 'Langue',
    sidebarEmail: 'E-mail',
    sidebarPassword: 'Sécurité',
    sidebarCompany: 'Entreprise',
    sidebarContract: 'Contrat',
    sidebarVpn: 'Réseau',
    sidebarComplete: 'Terminé',
    // Progress save
    progressSaved: 'Progression sauvegardée',
    resumeProgress: 'Reprendre où vous en étiez ?',
    resumeYes: 'Oui, reprendre',
    resumeNo: 'Non, recommencer',
    saving: 'Sauvegarde...',
    // Existing translations
    selectLanguage: 'Choisissez votre langue',
    languageDesc: 'Cette langue sera utilisée pour l\'interface et les rapports',
    continue: 'Continuer',
    back: 'Retour',
    emailVerified: 'E-mail vérifié !',
    welcome: 'Bienvenue',
    emailConfirmed: 'Votre adresse e-mail a été confirmée.',
    letsSetup: 'Configurons maintenant votre espace de travail.',
    startConfig: 'Commencer la configuration',
    createPassword: 'Créez votre mot de passe',
    passwordImportance: 'La sécurité de votre compte dépend de la robustesse de votre mot de passe. Un mot de passe fort protège vos données de pentest sensibles.',
    enableMFA: 'Activer l\'authentification à deux facteurs (2FA)',
    mfaDesc: 'Ajoutez une couche de sécurité supplémentaire avec une application d\'authentification',
    mfaRecommended: 'Fortement recommandé pour les comptes professionnels',
    passwordGenerator: 'Générateur de mot de passe',
    regenerate: 'Régénérer',
    classic: 'Classique',
    quantum: 'Anti-Quantique',
    chars: 'caractères',
    quantumDesc: 'Recommandé pour une sécurité maximale contre les futures attaques quantiques',
    orManual: 'ou saisissez manuellement',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    minChars: '8 caractères minimum',
    uppercase: 'Majuscule',
    lowercase: 'Minuscule',
    number: 'Chiffre',
    special: 'Caractère spécial',
    match: 'Mots de passe identiques',
    companyInfo: 'Informations entreprise',
    companyInfoDesc: 'Ces informations apparaîtront sur vos rapports de pentest',
    pentestFor: 'Utilisation de la plateforme',
    forMyOrg: 'Pour mon organisation',
    forMyOrgDesc: 'Je réalise des pentests pour sécuriser ma propre infrastructure',
    forClients: 'Pour mes clients',
    forClientsDesc: 'Je suis prestataire et réalise des pentests pour des clients externes',
    workspaceNote: 'Les workspaces seront organisés par sites/filiales',
    clientNote: 'Les workspaces seront organisés par clients',
    companyLogo: 'Logo de l\'entreprise',
    optional: 'optionnel',
    chooseFile: 'Choisir un fichier',
    sirenSiret: 'SIREN / SIRET',
    searchCompany: 'Rechercher une entreprise',
    searchByName: 'Rechercher par nom',
    searchPlaceholder: 'Nom de l\'entreprise...',
    searching: 'Recherche en cours...',
    noResults: 'Aucun résultat trouvé',
    selectCompany: 'Sélectionner',
    autoFilled: 'Informations pré-remplies via Pappers',
    address: 'Adresse',
    postalCode: 'Code postal',
    city: 'Ville',
    website: 'Site web',
    internalScans: 'Scans internes',
    internalScansDesc: 'Souhaitez-vous effectuer des scans sur votre réseau interne ?',
    yes: 'Oui',
    configureVpn: 'Configurer un VPN',
    no: 'Non',
    externalOnly: 'Scans externes uniquement',
    vpnType: 'Type de VPN',
    configFile: 'Fichier de configuration',
    dragFile: 'Glissez votre fichier ou',
    browse: 'parcourez',
    fileReady: 'Fichier prêt',
    configureLater: 'Configurer plus tard',
    choosePlan: 'Choisissez votre plan',
    choosePlanDesc: 'Sélectionnez l\'offre adaptée à vos besoins',
    stripeRedirect: 'Vous serez redirigé vers Stripe pour finaliser le paiement sécurisé',
    proceedPayment: 'Procéder au paiement',
    congrats: 'Félicitations !',
    spaceReady: 'Votre espace est prêt !',
    thankYou: 'Merci d\'avoir choisi SYLink Pentest. Votre plateforme de sécurité IA est configurée et prête à l\'emploi.',
    launchPlatform: 'Lancer ma plateforme de Pentest IA',
    preparing: 'Préparation de votre environnement...',
    dockerSetup: 'Configuration du conteneur Docker dédié...',
    needHelp: 'Besoin d\'aide ?',
    enableGuides: 'Activer les guides d\'accompagnement',
    guidesDesc: 'Des conseils contextuels vous aideront à chaque étape',
    configuration: 'Configuration',
    // Payment/SEPA step
    paymentMethod: 'Mode de paiement',
    paymentMethodDesc: 'Configurez le prélèvement automatique pour votre abonnement',
    sepaDirectDebit: 'Prélèvement SEPA',
    sepaDesc: 'Paiement mensuel automatique par prélèvement bancaire',
    ibanLabel: 'IBAN',
    ibanPlaceholder: 'FR76 XXXX XXXX XXXX XXXX XXXX XXX',
    bicLabel: 'BIC / SWIFT',
    bicPlaceholder: 'BNPAFRPP',
    accountHolderName: 'Titulaire du compte',
    accountHolderPlaceholder: 'Nom tel qu\'il apparaît sur le compte',
    sepaMandate: 'En fournissant vos informations bancaires, vous autorisez SYLink à envoyer des instructions à votre banque pour débiter votre compte conformément aux instructions.',
    sepaMandateTitle: 'Mandat de prélèvement SEPA',
    acceptMandate: 'J\'accepte le mandat de prélèvement SEPA',
    invalidIban: 'Format IBAN invalide',
    invalidBic: 'Format BIC invalide',
    paymentSecure: 'Vos données bancaires sont sécurisées et chiffrées',
    skipPayment: 'Configurer plus tard',
    billingAddress: 'Adresse de facturation',
    sameAsCompany: 'Identique à l\'adresse entreprise'
  },
  en: {
    // Step headers
    step1Title: 'Interface Language',
    step1Desc: 'Select the language that will be used throughout the application and your pentest reports.',
    step2Title: 'Email Successfully Verified',
    step2Desc: 'Your email address has been confirmed. You can now configure your secure workspace.',
    step3Title: 'Secure Your Account',
    step3Desc: 'Create a strong password to protect your sensitive pentest data. We recommend enabling 2FA.',
    step4Title: 'Company Information',
    step4Desc: 'This information will appear on your pentest reports and allows you to customize your workspace.',
    step5Title: 'Contract Signature',
    step5Desc: 'Read and accept the platform terms of use. SMS verification and your electronic signature are required.',
    step6Title: 'Internal Network Configuration',
    step6Desc: 'Configure a VPN to securely scan your internal infrastructure. This step is optional.',
    step7Title: 'Your Space is Ready!',
    step7Desc: 'Congratulations! Your AI pentest platform is configured. Launch it to start your security analyses.',
    // Contract step translations
    contractTitle: 'Terms of Use Agreement',
    contractSubtitle: 'Please read the terms and conditions carefully',
    scrollToAccept: 'Scroll to the bottom to accept',
    phoneVerification: 'SMS Verification',
    enterPhone: 'Phone number',
    sendCode: 'Send code',
    codeSent: 'Code sent!',
    enterCode: 'Verification code',
    verifyCode: 'Verify',
    codeVerified: 'Number verified',
    invalidCode: 'Invalid code',
    signatureTitle: 'Electronic Signature',
    signatureDesc: 'Sign in the box below with your mouse or finger',
    clearSignature: 'Clear',
    acceptContract: 'I accept the Terms of Use, Terms of Sale and Terms of Service',
    bothRequired: 'SMS verification and signature are required',
    contractAccepted: 'Contract accepted',
    // SMS Timer
    resendIn: 'Resend in',
    resendCode: 'Resend code',
    codeValidFor: 'Code valid for 10 minutes',
    // Email fallback
    smsNotReceived: 'SMS not received?',
    sendByEmail: 'Get code by email',
    sendingEmail: 'Sending...',
    codeSentByEmail: 'Code sent by email',
    emailSentTo: 'Email sent to',
    // Contract fullscreen
    viewFullscreen: 'Full screen',
    closeFullscreen: 'Close',
    // Signature
    signHere: 'Sign here',
    signatureGuide: 'Signature line',
    // Sidebar labels
    sidebarLanguage: 'Language',
    sidebarEmail: 'Email',
    sidebarPassword: 'Security',
    sidebarCompany: 'Company',
    sidebarContract: 'Contract',
    sidebarVpn: 'Network',
    sidebarComplete: 'Complete',
    // Progress save
    progressSaved: 'Progress saved',
    resumeProgress: 'Resume where you left off?',
    resumeYes: 'Yes, resume',
    resumeNo: 'No, start over',
    saving: 'Saving...',
    // Existing translations
    selectLanguage: 'Choose your language',
    languageDesc: 'This language will be used for the interface and reports',
    continue: 'Continue',
    back: 'Back',
    emailVerified: 'Email verified!',
    welcome: 'Welcome',
    emailConfirmed: 'Your email address has been confirmed.',
    letsSetup: 'Let\'s configure your workspace now.',
    startConfig: 'Start configuration',
    createPassword: 'Create your password',
    passwordImportance: 'Your account security depends on the strength of your password. A strong password protects your sensitive pentest data.',
    enableMFA: 'Enable two-factor authentication (2FA)',
    mfaDesc: 'Add an extra layer of security with an authenticator app',
    mfaRecommended: 'Strongly recommended for professional accounts',
    passwordGenerator: 'Password Generator',
    regenerate: 'Regenerate',
    classic: 'Classic',
    quantum: 'Quantum-Resistant',
    chars: 'characters',
    quantumDesc: 'Recommended for maximum security against future quantum attacks',
    orManual: 'or enter manually',
    password: 'Password',
    confirmPassword: 'Confirm password',
    minChars: '8 characters minimum',
    uppercase: 'Uppercase',
    lowercase: 'Lowercase',
    number: 'Number',
    special: 'Special character',
    match: 'Passwords match',
    companyInfo: 'Company information',
    companyInfoDesc: 'This information will appear on your pentest reports',
    pentestFor: 'Platform usage',
    forMyOrg: 'For my organization',
    forMyOrgDesc: 'I perform pentests to secure my own infrastructure',
    forClients: 'For my clients',
    forClientsDesc: 'I am a service provider performing pentests for external clients',
    workspaceNote: 'Workspaces will be organized by sites/branches',
    clientNote: 'Workspaces will be organized by clients',
    companyLogo: 'Company logo',
    optional: 'optional',
    chooseFile: 'Choose a file',
    sirenSiret: 'Company ID',
    searchCompany: 'Search for a company',
    searchByName: 'Search by name',
    searchPlaceholder: 'Company name...',
    searching: 'Searching...',
    noResults: 'No results found',
    selectCompany: 'Select',
    autoFilled: 'Information pre-filled via Pappers',
    address: 'Address',
    postalCode: 'Postal code',
    city: 'City',
    website: 'Website',
    internalScans: 'Internal scans',
    internalScansDesc: 'Do you want to perform scans on your internal network?',
    yes: 'Yes',
    configureVpn: 'Configure a VPN',
    no: 'No',
    externalOnly: 'External scans only',
    vpnType: 'VPN type',
    configFile: 'Configuration file',
    dragFile: 'Drag your file or',
    browse: 'browse',
    fileReady: 'File ready',
    configureLater: 'Configure later',
    choosePlan: 'Choose your plan',
    choosePlanDesc: 'Select the offer that fits your needs',
    stripeRedirect: 'You will be redirected to Stripe to complete the secure payment',
    proceedPayment: 'Proceed to payment',
    congrats: 'Congratulations!',
    spaceReady: 'Your space is ready!',
    thankYou: 'Thank you for choosing SYLink Pentest. Your AI security platform is configured and ready to use.',
    launchPlatform: 'Launch my AI Pentest platform',
    preparing: 'Preparing your environment...',
    dockerSetup: 'Setting up dedicated Docker container...',
    needHelp: 'Need help?',
    enableGuides: 'Enable guidance',
    guidesDesc: 'Contextual tips will help you at each step',
    configuration: 'Configuration',
    // Payment/SEPA step
    paymentMethod: 'Payment method',
    paymentMethodDesc: 'Set up automatic payment for your subscription',
    sepaDirectDebit: 'SEPA Direct Debit',
    sepaDesc: 'Automatic monthly payment by bank transfer',
    ibanLabel: 'IBAN',
    ibanPlaceholder: 'FR76 XXXX XXXX XXXX XXXX XXXX XXX',
    bicLabel: 'BIC / SWIFT',
    bicPlaceholder: 'BNPAFRPP',
    accountHolderName: 'Account holder name',
    accountHolderPlaceholder: 'Name as it appears on the account',
    sepaMandate: 'By providing your bank details, you authorize SYLink to send instructions to your bank to debit your account in accordance with the instructions.',
    sepaMandateTitle: 'SEPA Direct Debit Mandate',
    acceptMandate: 'I accept the SEPA direct debit mandate',
    invalidIban: 'Invalid IBAN format',
    invalidBic: 'Invalid BIC format',
    paymentSecure: 'Your bank details are secure and encrypted',
    skipPayment: 'Configure later',
    billingAddress: 'Billing address',
    sameAsCompany: 'Same as company address'
  },
  de: {
    selectLanguage: 'Wählen Sie Ihre Sprache',
    languageDesc: 'Diese Sprache wird für die Oberfläche und Berichte verwendet',
    continue: 'Weiter',
    back: 'Zurück',
    emailVerified: 'E-Mail verifiziert!',
    welcome: 'Willkommen',
    emailConfirmed: 'Ihre E-Mail-Adresse wurde bestätigt.',
    letsSetup: 'Lassen Sie uns jetzt Ihren Arbeitsbereich konfigurieren.',
    startConfig: 'Konfiguration starten',
    createPassword: 'Erstellen Sie Ihr Passwort',
    passwordImportance: 'Die Sicherheit Ihres Kontos hängt von der Stärke Ihres Passworts ab.',
    enableMFA: 'Zwei-Faktor-Authentifizierung (2FA) aktivieren',
    mfaDesc: 'Fügen Sie eine zusätzliche Sicherheitsebene hinzu',
    mfaRecommended: 'Für professionelle Konten dringend empfohlen',
    passwordGenerator: 'Passwort-Generator',
    regenerate: 'Neu generieren',
    classic: 'Klassisch',
    quantum: 'Quantensicher',
    chars: 'Zeichen',
    quantumDesc: 'Empfohlen für maximale Sicherheit',
    orManual: 'oder manuell eingeben',
    password: 'Passwort',
    confirmPassword: 'Passwort bestätigen',
    minChars: 'Mindestens 8 Zeichen',
    uppercase: 'Großbuchstabe',
    lowercase: 'Kleinbuchstabe',
    number: 'Zahl',
    special: 'Sonderzeichen',
    match: 'Passwörter stimmen überein',
    companyInfo: 'Unternehmensinformationen',
    companyInfoDesc: 'Diese Informationen erscheinen in Ihren Pentest-Berichten',
    pentestFor: 'Plattformnutzung',
    forMyOrg: 'Für meine Organisation',
    forMyOrgDesc: 'Ich führe Pentests durch, um meine eigene Infrastruktur zu sichern',
    forClients: 'Für meine Kunden',
    forClientsDesc: 'Ich bin Dienstleister und führe Pentests für externe Kunden durch',
    workspaceNote: 'Arbeitsbereiche werden nach Standorten organisiert',
    clientNote: 'Arbeitsbereiche werden nach Kunden organisiert',
    companyLogo: 'Firmenlogo',
    optional: 'optional',
    chooseFile: 'Datei auswählen',
    sirenSiret: 'Handelsregister-Nr.',
    address: 'Adresse',
    postalCode: 'Postleitzahl',
    city: 'Stadt',
    website: 'Website',
    internalScans: 'Interne Scans',
    internalScansDesc: 'Möchten Sie Scans in Ihrem internen Netzwerk durchführen?',
    yes: 'Ja',
    configureVpn: 'VPN konfigurieren',
    no: 'Nein',
    externalOnly: 'Nur externe Scans',
    vpnType: 'VPN-Typ',
    configFile: 'Konfigurationsdatei',
    dragFile: 'Datei hierher ziehen oder',
    browse: 'durchsuchen',
    fileReady: 'Datei bereit',
    configureLater: 'Später konfigurieren',
    choosePlan: 'Wählen Sie Ihren Plan',
    choosePlanDesc: 'Wählen Sie das passende Angebot',
    stripeRedirect: 'Sie werden zu Stripe weitergeleitet',
    proceedPayment: 'Zur Zahlung',
    congrats: 'Herzlichen Glückwunsch!',
    spaceReady: 'Ihr Bereich ist bereit!',
    thankYou: 'Vielen Dank, dass Sie sich für SYLink Pentest entschieden haben.',
    launchPlatform: 'Meine KI-Pentest-Plattform starten',
    preparing: 'Ihre Umgebung wird vorbereitet...',
    dockerSetup: 'Docker-Container wird konfiguriert...',
    needHelp: 'Brauchen Sie Hilfe?',
    enableGuides: 'Anleitungen aktivieren',
    guidesDesc: 'Kontextbezogene Tipps helfen Ihnen bei jedem Schritt',
    configuration: 'Konfiguration'
  },
  es: {
    selectLanguage: 'Elija su idioma',
    languageDesc: 'Este idioma se utilizará para la interfaz y los informes',
    continue: 'Continuar',
    back: 'Volver',
    emailVerified: '¡Email verificado!',
    welcome: 'Bienvenido',
    emailConfirmed: 'Su dirección de correo electrónico ha sido confirmada.',
    letsSetup: 'Configuremos ahora su espacio de trabajo.',
    startConfig: 'Iniciar configuración',
    createPassword: 'Cree su contraseña',
    passwordImportance: 'La seguridad de su cuenta depende de la fortaleza de su contraseña.',
    enableMFA: 'Activar autenticación de dos factores (2FA)',
    mfaDesc: 'Añada una capa adicional de seguridad',
    mfaRecommended: 'Muy recomendado para cuentas profesionales',
    passwordGenerator: 'Generador de contraseñas',
    regenerate: 'Regenerar',
    classic: 'Clásica',
    quantum: 'Cuántica',
    chars: 'caracteres',
    quantumDesc: 'Recomendado para máxima seguridad',
    orManual: 'o introduzca manualmente',
    password: 'Contraseña',
    confirmPassword: 'Confirmar contraseña',
    minChars: 'Mínimo 8 caracteres',
    uppercase: 'Mayúscula',
    lowercase: 'Minúscula',
    number: 'Número',
    special: 'Carácter especial',
    match: 'Las contraseñas coinciden',
    companyInfo: 'Información de la empresa',
    companyInfoDesc: 'Esta información aparecerá en sus informes de pentest',
    pentestFor: 'Uso de la plataforma',
    forMyOrg: 'Para mi organización',
    forMyOrgDesc: 'Realizo pentests para asegurar mi propia infraestructura',
    forClients: 'Para mis clientes',
    forClientsDesc: 'Soy proveedor y realizo pentests para clientes externos',
    workspaceNote: 'Los espacios de trabajo se organizarán por sitios',
    clientNote: 'Los espacios de trabajo se organizarán por clientes',
    companyLogo: 'Logo de la empresa',
    optional: 'opcional',
    chooseFile: 'Elegir archivo',
    sirenSiret: 'CIF/NIF',
    address: 'Dirección',
    postalCode: 'Código postal',
    city: 'Ciudad',
    website: 'Sitio web',
    internalScans: 'Escaneos internos',
    internalScansDesc: '¿Desea realizar escaneos en su red interna?',
    yes: 'Sí',
    configureVpn: 'Configurar VPN',
    no: 'No',
    externalOnly: 'Solo escaneos externos',
    vpnType: 'Tipo de VPN',
    configFile: 'Archivo de configuración',
    dragFile: 'Arrastre su archivo o',
    browse: 'examine',
    fileReady: 'Archivo listo',
    configureLater: 'Configurar más tarde',
    choosePlan: 'Elija su plan',
    choosePlanDesc: 'Seleccione la oferta que se adapte a sus necesidades',
    stripeRedirect: 'Será redirigido a Stripe para completar el pago seguro',
    proceedPayment: 'Proceder al pago',
    congrats: '¡Felicidades!',
    spaceReady: '¡Su espacio está listo!',
    thankYou: 'Gracias por elegir SYLink Pentest.',
    launchPlatform: 'Lanzar mi plataforma de Pentest IA',
    preparing: 'Preparando su entorno...',
    dockerSetup: 'Configurando contenedor Docker...',
    needHelp: '¿Necesita ayuda?',
    enableGuides: 'Activar guías',
    guidesDesc: 'Consejos contextuales le ayudarán en cada paso',
    configuration: 'Configuración'
  },
  it: {
    selectLanguage: 'Scegli la tua lingua',
    languageDesc: 'Questa lingua verrà utilizzata per l\'interfaccia e i report',
    continue: 'Continua',
    back: 'Indietro',
    emailVerified: 'Email verificata!',
    welcome: 'Benvenuto',
    emailConfirmed: 'Il tuo indirizzo email è stato confermato.',
    letsSetup: 'Configuriamo ora il tuo spazio di lavoro.',
    startConfig: 'Inizia configurazione',
    createPassword: 'Crea la tua password',
    passwordImportance: 'La sicurezza del tuo account dipende dalla robustezza della password.',
    enableMFA: 'Attiva autenticazione a due fattori (2FA)',
    mfaDesc: 'Aggiungi un ulteriore livello di sicurezza',
    mfaRecommended: 'Fortemente consigliato per account professionali',
    passwordGenerator: 'Generatore di password',
    regenerate: 'Rigenera',
    classic: 'Classica',
    quantum: 'Quantistica',
    chars: 'caratteri',
    quantumDesc: 'Consigliato per la massima sicurezza',
    orManual: 'o inserisci manualmente',
    password: 'Password',
    confirmPassword: 'Conferma password',
    minChars: 'Minimo 8 caratteri',
    uppercase: 'Maiuscola',
    lowercase: 'Minuscola',
    number: 'Numero',
    special: 'Carattere speciale',
    match: 'Le password coincidono',
    companyInfo: 'Informazioni azienda',
    companyInfoDesc: 'Queste informazioni appariranno nei tuoi report di pentest',
    pentestFor: 'Utilizzo della piattaforma',
    forMyOrg: 'Per la mia organizzazione',
    forMyOrgDesc: 'Eseguo pentest per proteggere la mia infrastruttura',
    forClients: 'Per i miei clienti',
    forClientsDesc: 'Sono un fornitore e eseguo pentest per clienti esterni',
    workspaceNote: 'Gli spazi di lavoro saranno organizzati per siti',
    clientNote: 'Gli spazi di lavoro saranno organizzati per clienti',
    companyLogo: 'Logo aziendale',
    optional: 'opzionale',
    chooseFile: 'Scegli file',
    sirenSiret: 'P.IVA',
    address: 'Indirizzo',
    postalCode: 'CAP',
    city: 'Città',
    website: 'Sito web',
    internalScans: 'Scansioni interne',
    internalScansDesc: 'Vuoi eseguire scansioni sulla tua rete interna?',
    yes: 'Sì',
    configureVpn: 'Configura VPN',
    no: 'No',
    externalOnly: 'Solo scansioni esterne',
    vpnType: 'Tipo di VPN',
    configFile: 'File di configurazione',
    dragFile: 'Trascina il file o',
    browse: 'sfoglia',
    fileReady: 'File pronto',
    configureLater: 'Configura più tardi',
    choosePlan: 'Scegli il tuo piano',
    choosePlanDesc: 'Seleziona l\'offerta adatta alle tue esigenze',
    stripeRedirect: 'Sarai reindirizzato a Stripe per completare il pagamento',
    proceedPayment: 'Procedi al pagamento',
    congrats: 'Congratulazioni!',
    spaceReady: 'Il tuo spazio è pronto!',
    thankYou: 'Grazie per aver scelto SYLink Pentest.',
    launchPlatform: 'Avvia la mia piattaforma Pentest IA',
    preparing: 'Preparazione del tuo ambiente...',
    dockerSetup: 'Configurazione container Docker...',
    needHelp: 'Hai bisogno di aiuto?',
    enableGuides: 'Attiva guide',
    guidesDesc: 'Suggerimenti contestuali ti aiuteranno ad ogni passo',
    configuration: 'Configurazione'
  },
  pt: {
    selectLanguage: 'Escolha seu idioma',
    languageDesc: 'Este idioma será usado para a interface e relatórios',
    continue: 'Continuar',
    back: 'Voltar',
    emailVerified: 'Email verificado!',
    welcome: 'Bem-vindo',
    emailConfirmed: 'Seu endereço de email foi confirmado.',
    letsSetup: 'Vamos configurar seu espaço de trabalho.',
    startConfig: 'Iniciar configuração',
    createPassword: 'Crie sua senha',
    passwordImportance: 'A segurança da sua conta depende da força da sua senha.',
    enableMFA: 'Ativar autenticação de dois fatores (2FA)',
    mfaDesc: 'Adicione uma camada extra de segurança',
    mfaRecommended: 'Altamente recomendado para contas profissionais',
    passwordGenerator: 'Gerador de senha',
    regenerate: 'Regenerar',
    classic: 'Clássica',
    quantum: 'Quântica',
    chars: 'caracteres',
    quantumDesc: 'Recomendado para máxima segurança',
    orManual: 'ou digite manualmente',
    password: 'Senha',
    confirmPassword: 'Confirmar senha',
    minChars: 'Mínimo 8 caracteres',
    uppercase: 'Maiúscula',
    lowercase: 'Minúscula',
    number: 'Número',
    special: 'Caractere especial',
    match: 'As senhas coincidem',
    companyInfo: 'Informações da empresa',
    companyInfoDesc: 'Estas informações aparecerão em seus relatórios de pentest',
    pentestFor: 'Uso da plataforma',
    forMyOrg: 'Para minha organização',
    forMyOrgDesc: 'Realizo pentests para proteger minha própria infraestrutura',
    forClients: 'Para meus clientes',
    forClientsDesc: 'Sou prestador e realizo pentests para clientes externos',
    workspaceNote: 'Os espaços de trabalho serão organizados por sites',
    clientNote: 'Os espaços de trabalho serão organizados por clientes',
    companyLogo: 'Logo da empresa',
    optional: 'opcional',
    chooseFile: 'Escolher arquivo',
    sirenSiret: 'CNPJ/NIF',
    address: 'Endereço',
    postalCode: 'CEP',
    city: 'Cidade',
    website: 'Site',
    internalScans: 'Varreduras internas',
    internalScansDesc: 'Deseja realizar varreduras em sua rede interna?',
    yes: 'Sim',
    configureVpn: 'Configurar VPN',
    no: 'Não',
    externalOnly: 'Apenas varreduras externas',
    vpnType: 'Tipo de VPN',
    configFile: 'Arquivo de configuração',
    dragFile: 'Arraste seu arquivo ou',
    browse: 'navegue',
    fileReady: 'Arquivo pronto',
    configureLater: 'Configurar depois',
    choosePlan: 'Escolha seu plano',
    choosePlanDesc: 'Selecione a oferta adequada às suas necessidades',
    stripeRedirect: 'Você será redirecionado ao Stripe para concluir o pagamento',
    proceedPayment: 'Prosseguir para pagamento',
    congrats: 'Parabéns!',
    spaceReady: 'Seu espaço está pronto!',
    thankYou: 'Obrigado por escolher SYLink Pentest.',
    launchPlatform: 'Iniciar minha plataforma de Pentest IA',
    preparing: 'Preparando seu ambiente...',
    dockerSetup: 'Configurando container Docker...',
    needHelp: 'Precisa de ajuda?',
    enableGuides: 'Ativar guias',
    guidesDesc: 'Dicas contextuais ajudarão você em cada etapa',
    configuration: 'Configuração'
  }
};

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0];
  return LANGUAGES.find(l => l.code === langCode)?.code || 'fr';
};

// Password Generator Component
const PasswordGenerator = ({ onSelect, t }) => {
  const [passwords, setPasswords] = useState({ classic: '', quantum: '' });
  const [copied, setCopied] = useState({ classic: false, quantum: false });
  const [selectedType, setSelectedType] = useState(null);

  const generateClassicPassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    const ensureChars = [
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)],
      'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)],
      '0123456789'[Math.floor(Math.random() * 10)],
      '!@#$%^&*'[Math.floor(Math.random() * 8)]
    ];
    return ensureChars.join('') + password.slice(4);
  };

  const generateQuantumPassword = () => {
    const length = 32;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?~`';
    let password = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    return password;
  };

  const regenerate = () => {
    setPasswords({
      classic: generateClassicPassword(),
      quantum: generateQuantumPassword()
    });
    setCopied({ classic: false, quantum: false });
    setSelectedType(null);
  };

  useEffect(() => {
    regenerate();
  }, []);

  const copyToClipboard = async (type) => {
    await navigator.clipboard.writeText(passwords[type]);
    setCopied({ ...copied, [type]: true });
    setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
  };

  const selectPassword = (type) => {
    setSelectedType(type);
    onSelect(passwords[type]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Key className="w-4 h-4 text-brand-green" />
          {t.passwordGenerator}
        </h4>
        <button
          type="button"
          onClick={regenerate}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          {t.regenerate}
        </button>
      </div>

      {/* Classic Password */}
      <div
        onClick={() => selectPassword('classic')}
        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selectedType === 'classic'
            ? 'border-brand-green bg-brand-green/10'
            : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-white">{t.classic}</span>
            <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">16 {t.chars}</span>
          </div>
          {selectedType === 'classic' && <Check className="w-5 h-5 text-brand-green" />}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 p-2 bg-gray-900 rounded text-sm text-gray-300 font-mono truncate">
            {passwords.classic}
          </code>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyToClipboard('classic'); }}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            {copied.classic ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4 text-gray-400" />}
          </button>
        </div>
      </div>

      {/* Quantum-Resistant Password */}
      <div
        onClick={() => selectPassword('quantum')}
        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selectedType === 'quantum'
            ? 'border-brand-green bg-brand-green/10'
            : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-white">{t.quantum}</span>
            <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">32 {t.chars}</span>
          </div>
          {selectedType === 'quantum' && <Check className="w-5 h-5 text-brand-green" />}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 p-2 bg-gray-900 rounded text-xs text-gray-300 font-mono truncate">
            {passwords.quantum}
          </code>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyToClipboard('quantum'); }}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            {copied.quantum ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4 text-gray-400" />}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t.quantumDesc}
        </p>
      </div>
    </div>
  );
};

// Plan Card Component
const PlanCard = ({ plan, selected, onSelect, recommended, planData }) => {
  const getIllustration = (planId) => {
    switch(planId) {
      case 'free':
        return (
          <div className="relative w-full h-24 mb-4 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-700/20 to-gray-800/20 rounded-xl" />
            <div className="relative flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gray-700/50 flex items-center justify-center">
                <Shield className="w-7 h-7 text-gray-400" />
              </div>
              <div className="text-left">
                <div className="w-20 h-2 bg-gray-700 rounded mb-1.5" />
                <div className="w-14 h-2 bg-gray-700/60 rounded" />
              </div>
            </div>
          </div>
        );
      case 'pro':
        return (
          <div className="relative w-full h-24 mb-4 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl" />
            <div className="relative flex items-center gap-2">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center animate-pulse">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Target className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-cyan-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'entreprise':
      default:
        return (
          <div className="relative w-full h-24 mb-4 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-green/10 via-emerald-500/10 to-cyan-500/10 rounded-xl" />
            <div className="relative flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-green/30 to-emerald-500/30 flex items-center justify-center shadow-lg shadow-brand-green/20">
                <Crown className="w-7 h-7 text-brand-green" />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs text-brand-green font-medium">Best Value</span>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  const getFeatures = (data) => {
    if (!data) return [];
    const features = [];
    if (data.id === 'free') {
      features.push('Simulation mode only');
    } else {
      features.push('Unlimited real scans');
    }
    if (data.max_workspaces === -1) {
      features.push('Unlimited workspaces');
    } else {
      features.push(`${data.max_workspaces} workspace${data.max_workspaces > 1 ? 's' : ''}`);
    }
    if (data.features?.tool_nmap) features.push('Tous les outils de scan');
    if (data.features?.custom_reports) features.push('Custom reports');
    if (data.features?.priority_support) features.push('Priority support');
    return features.slice(0, 5);
  };

  const p = planData || {
    id: plan,
    name: plan === 'free' ? 'Free' : plan === 'pro' ? 'Pro' : 'Enterprise',
    price_monthly: 0,
    max_workspaces: 1,
    features: {}
  };

  const features = getFeatures(p);

  return (
    <div
      onClick={() => onSelect(plan)}
      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
        selected
          ? 'border-brand-green bg-brand-green/5 scale-[1.02] shadow-lg shadow-brand-green/20'
          : 'border-gray-700/50 bg-gray-800/20 hover:border-gray-600 hover:bg-gray-800/30 hover:scale-[1.01]'
      }`}
    >
      {recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-brand-green to-emerald-400 text-black text-xs font-bold rounded-full shadow-lg shadow-brand-green/30">
          RECOMMANDE
        </div>
      )}
      {selected && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-brand-green flex items-center justify-center">
          <Check className="w-4 h-4 text-black" />
        </div>
      )}
      {getIllustration(p.id)}
      <h3 className="text-lg font-bold text-white mb-1">{p.name}</h3>
      <div className="flex items-baseline gap-1 mb-4">
        <span className={`text-3xl font-bold ${selected ? 'text-brand-green' : 'text-white'}`}>
          {p.price_monthly === 0 ? 'Gratuit' : `${p.price_monthly}EUR`}
        </span>
        {p.price_monthly > 0 && <span className="text-gray-400">/mois</span>}
      </div>
      <ul className="space-y-2">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
            <Check className={`w-4 h-4 flex-shrink-0 ${selected ? 'text-brand-green' : 'text-gray-500'}`} />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Step Header Component - Title and description at top of each step
const StepHeader = ({ icon, title, description, stepNumber, totalSteps }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
      <span className="px-2 py-1 bg-brand-green/10 text-brand-green rounded-lg font-medium">
        Step {stepNumber}/{totalSteps}
      </span>
    </div>
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
        <p className="text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

// Help Guide Panel
const HelpGuidePanel = ({ step, t, isOpen, onToggle }) => {
  const guides = {
    language: {
      title: 'Language selection',
      tips: [
        'The selected language will be used throughout the interface',
        'You can change it later in the settings',
        'Les rapports de pentest seront aussi dans cette langue'
      ]
    },
    password: {
      title: 'Password security',
      tips: [
        'Use the generator for an ultra-secure password',
        'The Anti-Quantum password offers protection against future attacks',
        'Enable 2FA for maximum security',
        'Never reuse this password elsewhere'
      ]
    },
    company: {
      title: 'Usage type',
      tips: [
        'For your organization: workspaces represent your sites/subsidiaries',
        'For your clients: each workspace = a client, with their own reports',
        'This distinction affects the organization of reports and dashboards'
      ]
    },
    vpn: {
      title: 'Internal scans',
      tips: [
        'VPN enables scanning your internal network securely',
        'We support OpenVPN, WireGuard and L2TP',
        'You can configure VPN later from the settings'
      ]
    },
    plan: {
      title: 'Plan selection',
      tips: [
        'The free plan lets you test the platform in simulation mode',
        'The Pro plan gives access to all real scanning tools',
        'You can upgrade at any time from your account'
      ]
    }
  };

  const currentGuide = guides[step] || guides.password;

  return (
    <div className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5 mt-6">
        <div className="flex items-start gap-4">
          <div className="p-2.5 bg-brand-green/20 rounded-xl">
            <BookOpen className="w-5 h-5 text-brand-green" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-white mb-3 text-base">{currentGuide.title}</h4>
            <ul className="space-y-2.5">
              {currentGuide.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/90">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-green" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main SetupWizard Component
const SetupWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language: globalLanguage } = useI18n();
  const token = searchParams.get('token');

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [showGuides, setShowGuides] = useState(true); // Enabled by default for new users

  // Pappers company search
  const [companySearch, setCompanySearch] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [companyAutoFilled, setCompanyAutoFilled] = useState(false);

  // Form data — initialize language from global i18n context if available, otherwise detect from browser
  const [formData, setFormData] = useState({
    language: globalLanguage || detectBrowserLanguage(),
    password: '',
    confirmPassword: '',
    showPassword: false,
    enableMFA: false,
    // Company details
    pentestType: null, // 'organization' or 'clients'
    company_address: '',
    company_city: '',
    company_postal_code: '',
    company_country: 'France',
    company_website: '',
    company_logo: null,
    company_siren: '',
    // VPN
    useVpn: null,
    vpnType: null,
    vpnConfig: null,
    vpnConfigName: '',
    // Plan (set aside - free by default)
    selectedPlan: 'free',
    // Contract signature
    contractPhone: '',
    contractOtp: '',
    contractOtpSent: false,
    contractOtpVerified: false,
    contractSignature: null,
    contractScrolledToBottom: false,
    contractAccepted: false
  });

  // Signature canvas ref
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  // SMS Timer state
  const [smsTimer, setSmsTimer] = useState(0);
  const [canResendSms, setCanResendSms] = useState(true);

  // Email fallback state
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailMask, setEmailMask] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Contract fullscreen modal
  const [contractFullscreen, setContractFullscreen] = useState(false);

  // Progress save toast
  const [showSaveToast, setShowSaveToast] = useState(false);

  // Resume modal
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);

  // Sync wizard language with global i18n language when it changes (and translation exists)
  useEffect(() => {
    if (globalLanguage && TRANSLATIONS[globalLanguage] && formData.language !== globalLanguage) {
      setFormData(prev => ({ ...prev, language: globalLanguage }));
    }
  }, [globalLanguage]); // eslint-disable-line

  // Get translations — use global i18n language as primary source, fall back to wizard's own selection
  const effectiveLanguage = formData.language || globalLanguage || 'fr';
  const t = TRANSLATIONS[effectiveLanguage] || TRANSLATIONS[globalLanguage] || TRANSLATIONS.fr;

  // Password validation
  const passwordValidation = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password),
      passwordsMatch: password === formData.confirmPassword && password.length > 0
    };
  }, [formData.password, formData.confirmPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(v => v);

  // Steps configuration (7 steps with contract signature) - Uses translated sidebar labels
  const steps = useMemo(() => [
    { id: 'language', title: t.sidebarLanguage || 'Langue', icon: <Languages className="w-5 h-5" /> },
    { id: 'email', title: t.sidebarEmail || 'Email', icon: <Mail className="w-5 h-5" /> },
    { id: 'password', title: t.sidebarPassword || 'Security', icon: <Lock className="w-5 h-5" /> },
    { id: 'company', title: t.sidebarCompany || 'Entreprise', icon: <Building2 className="w-5 h-5" /> },
    { id: 'contract', title: t.sidebarContract || 'Contrat', icon: <FileSignature className="w-5 h-5" /> },
    { id: 'vpn', title: t.sidebarVpn || 'Network', icon: <Network className="w-5 h-5" /> },
    { id: 'complete', title: t.sidebarComplete || 'Done', icon: <Rocket className="w-5 h-5" /> }
  ], [t]);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Verification token missing');
        setVerifying(false);
        return;
      }

      try {
        const response = await apiClient.get(`/auth/verify-setup-token/${token}`);
        setUserData(response.data);
        setVerifying(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired token');
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // SMS Timer countdown
  useEffect(() => {
    let interval;
    if (smsTimer > 0) {
      interval = setInterval(() => {
        setSmsTimer(prev => {
          if (prev <= 1) {
            setCanResendSms(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [smsTimer]);

  // Save progress to localStorage on step change
  useEffect(() => {
    if (token && currentStep > 0 && !verifying) {
      const progressData = {
        currentStep,
        formData: {
          ...formData,
          company_logo: null, // Don't save file objects
          vpnConfig: null,
          contractSignature: null // Don't save large base64 in localStorage
        },
        timestamp: Date.now()
      };
      localStorage.setItem(`setup_progress_${token}`, JSON.stringify(progressData));

      // Show save toast briefly
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 2000);
    }
  }, [currentStep, token, verifying]);

  // Check for saved progress on mount
  useEffect(() => {
    if (token && !verifying && userData) {
      const saved = localStorage.getItem(`setup_progress_${token}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Only offer to resume if less than 24 hours old and step > 0
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < 24 * 60 * 60 * 1000 && parsed.currentStep > 0) {
            setSavedProgress(parsed);
            setShowResumeModal(true);
          }
        } catch (e) {
          console.error('Failed to parse saved progress:', e);
        }
      }
    }
  }, [token, verifying, userData]);

  // Handle resume progress
  const handleResumeProgress = (resume) => {
    if (resume && savedProgress) {
      setCurrentStep(savedProgress.currentStep);
      setFormData(prev => ({
        ...prev,
        ...savedProgress.formData,
        company_logo: null,
        vpnConfig: null
      }));
    }
    setShowResumeModal(false);
    setSavedProgress(null);
  };

  // Clear saved progress on completion
  const clearSavedProgress = () => {
    if (token) {
      localStorage.removeItem(`setup_progress_${token}`);
    }
  };

  // Initialize signature canvas with guide line
  useEffect(() => {
    if (currentStep === 4 && signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Draw guide line
      ctx.strokeStyle = '#374151';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(40, canvas.height - 40);
      ctx.lineTo(canvas.width - 40, canvas.height - 40);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [currentStep]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    // Reset auto-filled flag when user manually edits
    if (['company_siren', 'company_address', 'company_city', 'company_postal_code'].includes(name)) {
      setCompanyAutoFilled(false);
    }
  };

  // Pappers company search by name
  const searchCompanyByName = async () => {
    if (!companySearch.trim() || companySearch.length < 2) return;

    setSearchingCompany(true);
    setCompanySearchResults([]);

    try {
      const response = await apiClient.get('/workspaces/pappers/search', {
        params: { name: companySearch }
      });
      setCompanySearchResults(response.data || []);
    } catch (err) {
      console.error('Company search failed:', err);
      setCompanySearchResults([]);
    } finally {
      setSearchingCompany(false);
    }
  };

  // Pappers lookup by SIREN
  const lookupBySiren = async (siren) => {
    const cleanSiren = siren.replace(/\s/g, '');
    if (cleanSiren.length < 9) return;

    setSearchingCompany(true);
    try {
      const endpoint = cleanSiren.length === 14
        ? `/workspaces/pappers/lookup/siret/${cleanSiren}`
        : `/workspaces/pappers/lookup/siren/${cleanSiren.substring(0, 9)}`;

      const response = await apiClient.get(endpoint);
      if (response.data) {
        selectCompanyFromPappers(response.data);
      }
    } catch (err) {
      console.error('SIREN lookup failed:', err);
    } finally {
      setSearchingCompany(false);
    }
  };

  // Select company from Pappers results
  const selectCompanyFromPappers = (company) => {
    setFormData(prev => ({
      ...prev,
      company_siren: company.siret || company.siren || '',
      company_address: company.address || '',
      company_city: company.city || '',
      company_postal_code: company.postal_code || '',
      company_website: company.website || ''
    }));
    setCompanyAutoFilled(true);
    setCompanySearchResults([]);
    setCompanySearch('');
  };

  const handlePasswordSelect = (password) => {
    setFormData(prev => ({
      ...prev,
      password,
      confirmPassword: password
    }));
  };

  const handleVpnFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        vpnConfig: file,
        vpnConfigName: file.name
      }));
    }
  };

  const nextStep = async () => {
    setError('');

    // Validate password step
    if (currentStep === 2 && !isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }

    // Save password
    if (currentStep === 2) {
      setLoading(true);
      try {
        await apiClient.post(`/auth/setup-password/${token}`, {
          password: formData.password,
          enable_mfa: formData.enableMFA
        });
      } catch (err) {
        setError(err.response?.data?.detail || 'Error while saving');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Save company details
    if (currentStep === 3) {
      setLoading(true);
      try {
        const companyData = new FormData();
        companyData.append('address', formData.company_address);
        companyData.append('city', formData.company_city);
        companyData.append('postal_code', formData.company_postal_code);
        companyData.append('country', formData.company_country);
        companyData.append('website', formData.company_website);
        companyData.append('siren', formData.company_siren);
        companyData.append('pentest_type', formData.pentestType || 'organization');
        if (formData.company_logo) {
          companyData.append('logo', formData.company_logo);
        }
        await apiClient.post(`/auth/setup-company/${token}`, companyData);
      } catch (err) {
        setError(err.response?.data?.detail || 'Error while saving');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Save contract signature (Step 4)
    if (currentStep === 4) {
      // Validate contract acceptance
      if (!formData.contractOtpVerified) {
        setError('Please verify your phone number via SMS');
        return;
      }
      if (!formData.contractSignature) {
        setError('Please sign the contract');
        return;
      }
      if (!formData.contractAccepted) {
        setError('Please accept the terms and conditions');
        return;
      }

      setLoading(true);
      try {
        await apiClient.post(`/auth/save-contract-signature/${token}`, {
          signature_data: formData.contractSignature,
          phone: formData.contractPhone
        });
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde de la signature');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Save VPN config (Step 5)
    if (currentStep === 5 && formData.useVpn && formData.vpnConfig) {
      setLoading(true);
      try {
        const vpnData = new FormData();
        vpnData.append('vpn_type', formData.vpnType);
        vpnData.append('config_file', formData.vpnConfig);
        await apiClient.post(`/auth/setup-vpn/${token}`, vpnData);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors de l\'upload VPN');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Save language preference
    if (currentStep === 0) {
      localStorage.setItem('preferredLanguage', formData.language);
    }

    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleLaunch = async () => {
    setLoading(true);
    try {
      // Assign free plan by default (Plan/Payment steps set aside)
      await apiClient.post(`/auth/setup-plan/${token}`, {
        plan: 'free'
      });

      await apiClient.post(`/auth/finalize-setup/${token}`);
      await new Promise(resolve => setTimeout(resolve, 3000));
      localStorage.setItem('showWelcomeGuide', 'true');
      navigate('/login?setup=complete');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error during launch');
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-green animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !userData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111] border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Lien invalide</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/register')}
            className="px-6 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-colors"
          >
            Nouvelle inscription
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Left Sidebar - Progress */}
      <div className="hidden lg:flex w-80 bg-[#0d0d0d] border-r border-gray-800 flex-col">
        <div className="p-6 border-b border-gray-800">
          <img src="/logo.png" alt="SYLINK" className="h-10 w-auto" />
        </div>

        <div className="flex-1 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
              {t.configuration}
            </h3>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3" />
              ~5 min
            </span>
          </div>
          <div className="space-y-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                  index === currentStep
                    ? 'bg-brand-green/10 text-brand-green'
                    : index < currentStep
                    ? 'text-brand-green/60'
                    : 'text-gray-500'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  index === currentStep
                    ? 'bg-brand-green text-black'
                    : index < currentStep
                    ? 'bg-brand-green/20'
                    : 'bg-gray-800'
                }`}>
                  {index < currentStep ? <Check className="w-4 h-4" /> : step.icon}
                </div>
                <span className="font-medium">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Help section */}
        <div className="p-6 border-t border-gray-800">
          <button
            onClick={() => setShowGuides(!showGuides)}
            className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
              showGuides ? 'bg-blue-500/10 text-blue-400' : 'hover:bg-gray-800 text-gray-400'
            }`}
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5" />
              <span className="text-sm font-medium">{t.needHelp}</span>
            </div>
            <div className={`w-10 h-6 rounded-full transition-all ${showGuides ? 'bg-blue-500' : 'bg-gray-700'} relative`}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${showGuides ? 'left-5' : 'left-1'}`} />
            </div>
          </button>
          {showGuides && (
            <p className="mt-2 text-xs text-gray-500 px-3">{t.guidesDesc}</p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <div className="lg:hidden p-4 border-b border-gray-800 flex justify-between items-center">
          <img src="/logo.png" alt="SYLINK" className="h-8 w-auto" />
          <button
            onClick={() => setShowGuides(!showGuides)}
            className={`p-2 rounded-lg ${showGuides ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400'}`}
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar Mobile */}
        <div className="lg:hidden p-4">
          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full ${
                  index <= currentStep ? 'bg-brand-green' : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-sm text-gray-400">
              Step {currentStep + 1} of {steps.length}
            </p>
            <span className="text-xs text-brand-green font-medium">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
          <div className="w-full max-w-3xl">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Step 0: Language Selection */}
            {currentStep === 0 && (
              <div>
                <StepHeader
                  icon={<Languages className="w-7 h-7 text-brand-green" />}
                  title={t.step1Title}
                  description={t.step1Desc}
                  stepNumber={1}
                  totalSteps={steps.length}
                />

                <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setFormData(prev => ({ ...prev, language: lang.code }))}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        formData.language === lang.code
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <span className="text-2xl">{lang.flag}</span>
                      <span className={`font-medium ${formData.language === lang.code ? 'text-brand-green' : 'text-white'}`}>
                        {lang.name}
                      </span>
                      {formData.language === lang.code && (
                        <Check className="w-5 h-5 text-brand-green ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                <button
                  onClick={nextStep}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-brand-green text-black font-bold rounded-xl hover:bg-brand-green/90 transition-all transform hover:scale-105"
                >
                  {t.continue}
                  <ArrowRight className="w-5 h-5" />
                </button>

                <HelpGuidePanel step="language" t={t} isOpen={showGuides} />
              </div>
            )}

            {/* Step 1: Email Verification */}
            {currentStep === 1 && (
              <div>
                <StepHeader
                  icon={<CheckCircle className="w-7 h-7 text-brand-green" />}
                  title={t.step2Title}
                  description={t.step2Desc}
                  stepNumber={2}
                  totalSteps={steps.length}
                />

                <div className="text-center p-8 bg-gradient-to-br from-brand-green/5 to-cyan-500/5 border border-brand-green/20 rounded-2xl mb-8">
                  <p className="text-gray-300 mb-2">
                    {t.welcome} <span className="text-white font-semibold text-lg">{userData?.first_name}</span> !
                  </p>
                  <p className="text-brand-green font-medium">{userData?.email}</p>
                </div>

                <div className="text-center">
                  <button
                    onClick={nextStep}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-brand-green text-black font-bold rounded-xl hover:bg-brand-green/90 transition-all transform hover:scale-105"
                  >
                    {t.startConfig}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Password */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <StepHeader
                  icon={<Lock className="w-7 h-7 text-brand-green" />}
                  title={t.step3Title}
                  description={t.step3Desc}
                  stepNumber={3}
                  totalSteps={steps.length}
                />

                {/* MFA Option */}
                <div className="p-5 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <div className="relative mt-1">
                      <input
                        type="checkbox"
                        checked={formData.enableMFA}
                        onChange={(e) => setFormData(prev => ({ ...prev, enableMFA: e.target.checked }))}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-all ${formData.enableMFA ? 'bg-purple-500' : 'bg-gray-700'}`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${formData.enableMFA ? 'left-7' : 'left-1'}`} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Smartphone className="w-4 h-4 text-purple-400" />
                        <span className="font-medium text-white">{t.enableMFA}</span>
                      </div>
                      <p className="text-sm text-purple-300/80">{t.mfaDesc}</p>
                      <p className="text-xs text-purple-400 mt-1">{t.mfaRecommended}</p>
                    </div>
                  </label>
                </div>

                {/* Manual Password Input */}
                <div className="space-y-4 p-6 bg-gray-800/30 rounded-xl border border-gray-700">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.password}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type={formData.showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-gray-700 rounded-xl pl-12 pr-12 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder={t.password}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {formData.showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.confirmPassword}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type={formData.showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`w-full bg-[#111] border rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                          formData.confirmPassword
                            ? passwordValidation.passwordsMatch
                              ? 'border-brand-green/50 focus:ring-brand-green/20'
                              : 'border-red-500/50 focus:ring-red-500/20'
                            : 'border-gray-700 focus:border-brand-green/50 focus:ring-brand-green/20'
                        }`}
                        placeholder={t.confirmPassword}
                      />
                    </div>
                  </div>

                  {/* Password Requirements */}
                  {formData.password && (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`flex items-center gap-1 ${passwordValidation.minLength ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.minChars}
                      </div>
                      <div className={`flex items-center gap-1 ${passwordValidation.hasUppercase ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.uppercase}
                      </div>
                      <div className={`flex items-center gap-1 ${passwordValidation.hasLowercase ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.lowercase}
                      </div>
                      <div className={`flex items-center gap-1 ${passwordValidation.hasNumber ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.number}
                      </div>
                      <div className={`flex items-center gap-1 ${passwordValidation.hasSpecial ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.special}
                      </div>
                      <div className={`flex items-center gap-1 ${passwordValidation.passwordsMatch ? 'text-brand-green' : 'text-gray-500'}`}>
                        <CheckCircle className="w-3 h-3" /> {t.match}
                      </div>
                    </div>
                  )}
                </div>

                <div className="my-6 flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-700" />
                  <span className="text-sm text-gray-500">{t.orManual}</span>
                  <div className="flex-1 h-px bg-gray-700" />
                </div>

                {/* Password Generator Below */}
                <PasswordGenerator onSelect={handlePasswordSelect} t={t} />

                <HelpGuidePanel step="password" t={t} isOpen={showGuides} />
              </div>
            )}

            {/* Step 3: Company Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <StepHeader
                  icon={<Building2 className="w-7 h-7 text-brand-green" />}
                  title={t.step4Title}
                  description={t.step4Desc}
                  stepNumber={4}
                  totalSteps={steps.length}
                />

                {/* Pentest Type Selection */}
                <div className="p-6 bg-gray-800/30 rounded-xl border border-gray-700">
                  <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-brand-green" />
                    {t.pentestFor}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, pentestType: 'organization' }))}
                      className={`p-5 rounded-xl border-2 transition-all text-left ${
                        formData.pentestType === 'organization'
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${formData.pentestType === 'organization' ? 'bg-brand-green/20' : 'bg-gray-700'}`}>
                          <Building2 className={`w-5 h-5 ${formData.pentestType === 'organization' ? 'text-brand-green' : 'text-gray-400'}`} />
                        </div>
                        <span className="font-medium text-white">{t.forMyOrg}</span>
                      </div>
                      <p className="text-sm text-gray-400">{t.forMyOrgDesc}</p>
                      {formData.pentestType === 'organization' && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-brand-green">
                          <Info className="w-3.5 h-3.5" />
                          {t.workspaceNote}
                        </div>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, pentestType: 'clients' }))}
                      className={`p-5 rounded-xl border-2 transition-all text-left ${
                        formData.pentestType === 'clients'
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${formData.pentestType === 'clients' ? 'bg-brand-green/20' : 'bg-gray-700'}`}>
                          <Users className={`w-5 h-5 ${formData.pentestType === 'clients' ? 'text-brand-green' : 'text-gray-400'}`} />
                        </div>
                        <span className="font-medium text-white">{t.forClients}</span>
                      </div>
                      <p className="text-sm text-gray-400">{t.forClientsDesc}</p>
                      {formData.pentestType === 'clients' && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-brand-green">
                          <Info className="w-3.5 h-3.5" />
                          {t.clientNote}
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Pappers Company Search */}
                  <div className="p-4 bg-gradient-to-r from-brand-green/10 to-emerald-500/10 border border-brand-green/30 rounded-xl">
                    <label className="block text-sm font-medium text-brand-green mb-2 flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      {t.searchCompany}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchCompanyByName()}
                        className="flex-1 bg-[#111] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder={t.searchPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={searchCompanyByName}
                        disabled={searchingCompany || companySearch.length < 2}
                        className="px-4 py-3 bg-brand-green hover:bg-brand-green/90 disabled:bg-gray-700 disabled:text-gray-500 text-black font-medium rounded-xl transition-colors flex items-center gap-2"
                      >
                        {searchingCompany ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Search className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    {/* Search Results */}
                    {companySearchResults.length > 0 && (
                      <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
                        {companySearchResults.map((company, idx) => (
                          <div
                            key={idx}
                            onClick={() => selectCompanyFromPappers(company)}
                            className="p-3 bg-gray-800/80 hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
                          >
                            <div className="font-medium text-white">{company.company_name}</div>
                            <div className="text-xs text-gray-400 flex items-center gap-2 mt-1">
                              <span>{company.siren}</span>
                              {company.city && <span>• {company.city}</span>}
                              {company.legal_form && <span>• {company.legal_form}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchingCompany && (
                      <div className="mt-3 text-center text-gray-400 text-sm">
                        {t.searching}
                      </div>
                    )}
                  </div>

                  {/* Auto-filled indicator */}
                  {companyAutoFilled && (
                    <div className="flex items-center gap-2 text-sm text-brand-green bg-brand-green/10 px-3 py-2 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      {t.autoFilled}
                    </div>
                  )}

                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.companyLogo} <span className="text-gray-500">({t.optional})</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center overflow-hidden">
                        {formData.company_logo ? (
                          <img
                            src={URL.createObjectURL(formData.company_logo)}
                            alt="Logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Image className="w-8 h-8 text-gray-500" />
                        )}
                      </div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          name="company_logo"
                          accept="image/*"
                          onChange={handleChange}
                          className="hidden"
                        />
                        <span className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors inline-block">
                          {t.chooseFile}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* SIREN with lookup */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.sirenSiret} <span className="text-gray-500">({t.optional})</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="company_siren"
                        value={formData.company_siren}
                        onChange={handleChange}
                        className="flex-1 bg-[#111] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="123 456 789 00012"
                      />
                      <button
                        type="button"
                        onClick={() => lookupBySiren(formData.company_siren)}
                        disabled={searchingCompany || formData.company_siren.replace(/\s/g, '').length < 9}
                        className="px-4 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded-xl transition-colors"
                        title="Rechercher"
                      >
                        {searchingCompany ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Search className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.address} <span className="text-gray-500">({t.optional})</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-3 w-5 h-5 text-gray-500" />
                      <input
                        type="text"
                        name="company_address"
                        value={formData.company_address}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="123 Security Street"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t.postalCode}
                      </label>
                      <input
                        type="text"
                        name="company_postal_code"
                        value={formData.company_postal_code}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="75001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t.city}
                      </label>
                      <input
                        type="text"
                        name="company_city"
                        value={formData.company_city}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="Paris"
                      />
                    </div>
                  </div>

                  {/* Website */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t.website} <span className="text-gray-500">({t.optional})</span>
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="url"
                        name="company_website"
                        value={formData.company_website}
                        onChange={handleChange}
                        className="w-full bg-[#111] border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="https://www.exemple.com"
                      />
                    </div>
                  </div>
                </div>

                <HelpGuidePanel step="company" t={t} isOpen={showGuides} />
              </div>
            )}

            {/* Step 4: Contract Signature */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <StepHeader
                  icon={<FileSignature className="w-7 h-7 text-brand-green" />}
                  title={t.step5Title}
                  description={t.step5Desc}
                  stepNumber={5}
                  totalSteps={steps.length}
                />

                {/* Contract Text */}
                <div className="bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden">
                  <div className="bg-gray-800 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ScrollText className="w-5 h-5 text-brand-green" />
                      <span className="font-medium text-white">{t.contractTitle}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {!formData.contractScrolledToBottom && (
                        <span className="text-xs text-amber-400 flex items-center gap-1">
                          <ArrowRight className="w-3 h-3 animate-bounce" />
                          {t.scrollToAccept}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setContractFullscreen(true)}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                      >
                        <Maximize2 className="w-3.5 h-3.5" />
                        {t.viewFullscreen}
                      </button>
                    </div>
                  </div>
                  <div
                    className="h-80 overflow-y-auto p-4 text-sm text-gray-300 contract-scroll"
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target;
                      if (scrollTop + clientHeight >= scrollHeight - 20) {
                        setFormData(prev => ({ ...prev, contractScrolledToBottom: true }));
                      }
                    }}
                  >
                    <div className="prose prose-invert prose-sm max-w-none">
                      <h1 className="text-lg font-bold text-white mb-4">CONTRAT D'UTILISATION DE LA PLATEFORME SYLINK PENTEST</h1>
                      <h2 className="text-base font-semibold text-brand-green mb-2">CONDITIONS GÉNÉRALES D'UTILISATION ET LIMITATION DE RESPONSABILITÉ</h2>

                      <p className="text-gray-400 mb-4">
                        <strong>SYLINK TECHNOLOGIE SAS</strong><br />
                        SIREN : 829 104 595 | SIRET : 829 104 595 00022<br />
                        Capital social : 217 065,00 € | RCS Clermont-Ferrand<br />
                        35 Rue Blatin, 63000 Clermont-Ferrand, France
                      </p>

                      <h3 className="text-sm font-semibold text-white mt-4 mb-2">PRÉAMBULE</h3>
                      <p>Le présent contrat définit les conditions d'utilisation de la plateforme SYLink Pentest, éditée et opérée par SYLink Technologie SAS. La Plateforme est une solution professionnelle d'automatisation des tests d'intrusion destinée exclusivement aux professionnels de la cybersécurité.</p>

                      <p className="text-amber-300 font-semibold my-3">L'acceptation du présent Contrat est un préalable obligatoire à toute utilisation de la Plateforme.</p>

                      <h3 className="text-sm font-semibold text-white mt-4 mb-2">PARTIE I - CADRE LÉGAL</h3>
                      <p><strong>Article 323-1 du Code Pénal :</strong> Le fait d'accéder ou de se maintenir, frauduleusement, dans tout ou partie d'un système de traitement automatisé de données est puni de trois ans d'emprisonnement et de 100 000 euros d'amende.</p>

                      <p><strong>Article 323-2 du Code Pénal :</strong> Le fait d'entraver ou de fausser le fonctionnement d'un système de traitement automatisé de données est puni de cinq ans d'emprisonnement et de 150 000 euros d'amende.</p>

                      <p className="text-red-400 font-semibold my-3">AVERTISSEMENT : La réalisation de tests d'intrusion sur un système informatique sans autorisation explicite constitue une infraction pénale.</p>

                      <h3 className="text-sm font-semibold text-white mt-4 mb-2">PARTIE II - RESPONSABILITÉS</h3>
                      <p><strong>L'UTILISATEUR EST SEUL ET UNIQUE RESPONSABLE</strong> de l'utilisation qu'il fait de la Plateforme SYLink Pentest et des actions qu'il entreprend au moyen de celle-ci.</p>

                      <p>En utilisant la Plateforme, l'Utilisateur garantit :</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Disposer de toutes les autorisations légales nécessaires</li>
                        <li>Respecter scrupuleusement le périmètre défini dans les autorisations</li>
                        <li>Ne pas utiliser la Plateforme à des fins illicites ou malveillantes</li>
                        <li>Disposer des compétences techniques nécessaires</li>
                      </ul>

                      <h3 className="text-sm font-semibold text-white mt-4 mb-2">PARTIE III - LIMITATION DE RESPONSABILITÉ</h3>
                      <p><strong>SYLINK TECHNOLOGIE SAS DÉCLINE TOUTE RESPONSABILITÉ</strong> quant aux actions entreprises par l'Utilisateur au moyen de la Plateforme.</p>

                      <p>SYLink Technologie SAS ne saurait être tenue responsable des dommages directs ou indirects résultant de l'utilisation de la Plateforme.</p>

                      <h3 className="text-sm font-semibold text-white mt-4 mb-2">ACCEPTATION DU CONTRAT</h3>
                      <p>En signant ce contrat, l'Utilisateur reconnaît :</p>
                      <ol className="list-decimal pl-5 space-y-1">
                        <li>Avoir lu et compris l'intégralité du présent Contrat</li>
                        <li>Accepter sans réserve l'ensemble de ses dispositions</li>
                        <li>Être informé du cadre légal applicable aux tests d'intrusion</li>
                        <li>Comprendre que l'obtention d'autorisations préalables est obligatoire</li>
                        <li>Assumer l'entière responsabilité de l'utilisation de la Plateforme</li>
                        <li>Dégager SYLink Technologie SAS de toute responsabilité</li>
                      </ol>

                      <p className="text-gray-500 mt-6 text-xs">
                        Document contractuel - Tous droits réservés - SYLink Technologie SAS © 2017-2026<br />
                        Date de dernière mise à jour : Janvier 2026
                      </p>
                    </div>
                  </div>
                </div>

                {/* SMS Verification */}
                <div className="p-6 bg-gray-800/30 rounded-xl border border-gray-700">
                  <h3 className="text-base font-medium text-white mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-brand-green" />
                    {t.phoneVerification}
                    {formData.contractOtpVerified && (
                      <span className="ml-auto flex items-center gap-1 text-sm text-brand-green">
                        <CheckCircle className="w-4 h-4" />
                        {t.codeVerified}
                      </span>
                    )}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Phone Input */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">{t.enterPhone}</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <input
                            type="tel"
                            value={formData.contractPhone}
                            onChange={(e) => setFormData(prev => ({ ...prev, contractPhone: e.target.value }))}
                            disabled={formData.contractOtpVerified}
                            className="w-full bg-[#111] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 disabled:opacity-50"
                            placeholder="+33 6 12 34 56 78"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!formData.contractPhone || !canResendSms) return;
                            setLoading(true);
                            try {
                              await apiClient.post(`/auth/send-contract-otp/${token}`, {
                                phone: formData.contractPhone
                              });
                              setFormData(prev => ({ ...prev, contractOtpSent: true }));
                              // Start 60 second timer
                              setSmsTimer(60);
                              setCanResendSms(false);
                            } catch (err) {
                              setError(err.response?.data?.detail || 'Erreur envoi SMS');
                            }
                            setLoading(false);
                          }}
                          disabled={!formData.contractPhone || formData.contractOtpVerified || loading || !canResendSms}
                          className="px-4 py-2.5 bg-brand-green hover:bg-brand-green/90 disabled:bg-gray-700 disabled:text-gray-500 text-black font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          {formData.contractOtpSent && !canResendSms ? `${t.resendIn} ${smsTimer}s` : formData.contractOtpSent ? t.resendCode : t.sendCode}
                        </button>
                      </div>
                      {formData.contractOtpSent && !formData.contractOtpVerified && (
                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {t.codeValidFor}
                        </p>
                      )}
                    </div>

                    {/* OTP Input */}
                    {formData.contractOtpSent && !formData.contractOtpVerified && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">{t.enterCode}</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={formData.contractOtp}
                            onChange={(e) => setFormData(prev => ({ ...prev, contractOtp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                            className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-4 py-2.5 text-white text-center text-lg tracking-widest font-mono placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
                            placeholder="000000"
                            maxLength={6}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              if (formData.contractOtp.length !== 6) return;
                              setLoading(true);
                              try {
                                await apiClient.post(`/auth/verify-contract-otp/${token}`, {
                                  phone: formData.contractPhone,
                                  otp: formData.contractOtp
                                });
                                setFormData(prev => ({ ...prev, contractOtpVerified: true }));
                                setError('');
                              } catch (err) {
                                setError(t.invalidCode);
                              }
                              setLoading(false);
                            }}
                            disabled={formData.contractOtp.length !== 6 || loading}
                            className="px-4 py-2.5 bg-brand-green hover:bg-brand-green/90 disabled:bg-gray-700 disabled:text-gray-500 text-black font-medium rounded-lg transition-colors"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.verifyCode}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email fallback option */}
                  {formData.contractOtpSent && !formData.contractOtpVerified && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      {emailCodeSent ? (
                        <div className="flex items-center gap-2 text-sm text-brand-green">
                          <Mail className="w-4 h-4" />
                          <span>{t.codeSentByEmail}</span>
                          {emailMask && <span className="text-gray-400">({t.emailSentTo} {emailMask})</span>}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-400">{t.smsNotReceived}</span>
                          <button
                            type="button"
                            onClick={async () => {
                              setSendingEmail(true);
                              try {
                                const response = await apiClient.post(`/auth/send-contract-otp-email/${token}`);
                                setEmailCodeSent(true);
                                setEmailMask(response.data.email || '');
                              } catch (err) {
                                setError(err.response?.data?.detail || 'Erreur envoi email');
                              }
                              setSendingEmail(false);
                            }}
                            disabled={sendingEmail}
                            className="text-brand-green hover:text-brand-green/80 underline underline-offset-2 flex items-center gap-1 transition-colors"
                          >
                            {sendingEmail ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {t.sendingEmail}
                              </>
                            ) : (
                              <>
                                <Mail className="w-3 h-3" />
                                {t.sendByEmail}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Signature Pad */}
                <div className="p-6 bg-gray-800/30 rounded-xl border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-medium text-white flex items-center gap-2">
                      <FileSignature className="w-5 h-5 text-brand-green" />
                      {t.signatureTitle}
                      {!signatureEmpty && (
                        <span className="ml-2 text-xs text-brand-green bg-brand-green/10 px-2 py-0.5 rounded-full">
                          <Check className="w-3 h-3 inline mr-1" />
                          Signed
                        </span>
                      )}
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = signatureCanvasRef.current;
                        if (canvas) {
                          const ctx = canvas.getContext('2d');
                          ctx.fillStyle = '#1a1a1a';
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                          // Redraw guide line
                          ctx.strokeStyle = '#374151';
                          ctx.lineWidth = 1;
                          ctx.setLineDash([5, 5]);
                          ctx.beginPath();
                          ctx.moveTo(40, canvas.height - 40);
                          ctx.lineTo(canvas.width - 40, canvas.height - 40);
                          ctx.stroke();
                          ctx.setLineDash([]);
                          setSignatureEmpty(true);
                          setFormData(prev => ({ ...prev, contractSignature: null }));
                        }
                      }}
                      className="text-sm text-gray-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                    >
                      <Eraser className="w-4 h-4" />
                      {t.clearSignature}
                    </button>
                  </div>
                  <p className="text-sm text-gray-400 mb-3">{t.signatureDesc}</p>
                  <div className="relative bg-[#1a1a1a] rounded-lg border-2 border-dashed border-gray-600 overflow-hidden">
                    <canvas
                      ref={signatureCanvasRef}
                      width={600}
                      height={180}
                      className="w-full cursor-crosshair"
                      style={{ touchAction: 'none', minHeight: '150px' }}
                      onMouseDown={(e) => {
                        setIsDrawing(true);
                        const canvas = signatureCanvasRef.current;
                        const rect = canvas.getBoundingClientRect();
                        const ctx = canvas.getContext('2d');
                        ctx.beginPath();
                        ctx.moveTo(
                          (e.clientX - rect.left) * (canvas.width / rect.width),
                          (e.clientY - rect.top) * (canvas.height / rect.height)
                        );
                      }}
                      onMouseMove={(e) => {
                        if (!isDrawing) return;
                        const canvas = signatureCanvasRef.current;
                        const rect = canvas.getBoundingClientRect();
                        const ctx = canvas.getContext('2d');
                        ctx.strokeStyle = '#00ff88';
                        ctx.lineWidth = 2;
                        ctx.lineCap = 'round';
                        ctx.lineTo(
                          (e.clientX - rect.left) * (canvas.width / rect.width),
                          (e.clientY - rect.top) * (canvas.height / rect.height)
                        );
                        ctx.stroke();
                        setSignatureEmpty(false);
                      }}
                      onMouseUp={() => {
                        setIsDrawing(false);
                        const canvas = signatureCanvasRef.current;
                        setFormData(prev => ({ ...prev, contractSignature: canvas.toDataURL() }));
                      }}
                      onMouseLeave={() => {
                        if (isDrawing) {
                          setIsDrawing(false);
                          const canvas = signatureCanvasRef.current;
                          setFormData(prev => ({ ...prev, contractSignature: canvas.toDataURL() }));
                        }
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setIsDrawing(true);
                        const canvas = signatureCanvasRef.current;
                        const rect = canvas.getBoundingClientRect();
                        const touch = e.touches[0];
                        const ctx = canvas.getContext('2d');
                        ctx.beginPath();
                        ctx.moveTo(
                          (touch.clientX - rect.left) * (canvas.width / rect.width),
                          (touch.clientY - rect.top) * (canvas.height / rect.height)
                        );
                      }}
                      onTouchMove={(e) => {
                        e.preventDefault();
                        if (!isDrawing) return;
                        const canvas = signatureCanvasRef.current;
                        const rect = canvas.getBoundingClientRect();
                        const touch = e.touches[0];
                        const ctx = canvas.getContext('2d');
                        ctx.strokeStyle = '#00ff88';
                        ctx.lineWidth = 2;
                        ctx.lineCap = 'round';
                        ctx.lineTo(
                          (touch.clientX - rect.left) * (canvas.width / rect.width),
                          (touch.clientY - rect.top) * (canvas.height / rect.height)
                        );
                        ctx.stroke();
                        setSignatureEmpty(false);
                      }}
                      onTouchEnd={() => {
                        setIsDrawing(false);
                        const canvas = signatureCanvasRef.current;
                        setFormData(prev => ({ ...prev, contractSignature: canvas.toDataURL() }));
                      }}
                    />
                    {signatureEmpty && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-gray-600 text-sm">{t.signHere}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Accept Checkbox */}
                <div className="p-4 bg-brand-green/10 border border-brand-green/30 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.contractAccepted}
                      onChange={(e) => setFormData(prev => ({ ...prev, contractAccepted: e.target.checked }))}
                      disabled={!formData.contractScrolledToBottom || !formData.contractOtpVerified || signatureEmpty}
                      className="w-5 h-5 mt-0.5 rounded border-gray-600 bg-[#111111] text-brand-green focus:ring-brand-green focus:ring-offset-0 disabled:opacity-50"
                    />
                    <span className={`text-sm ${formData.contractAccepted ? 'text-brand-green font-medium' : 'text-gray-300'}`}>
                      {t.acceptContract}
                    </span>
                  </label>
                  {(!formData.contractOtpVerified || signatureEmpty) && (
                    <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t.bothRequired}
                    </p>
                  )}
                </div>

                <HelpGuidePanel step="company" t={t} isOpen={showGuides} />
              </div>
            )}

            {/* Step 5: VPN Configuration */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <StepHeader
                  icon={<Network className="w-7 h-7 text-brand-green" />}
                  title={t.step6Title}
                  description={t.step6Desc}
                  stepNumber={6}
                  totalSteps={steps.length}
                />

                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, useVpn: true }))}
                    className={`p-6 rounded-xl border-2 transition-all ${
                      formData.useVpn === true
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                    }`}
                  >
                    <Network className={`w-8 h-8 mx-auto mb-3 ${formData.useVpn === true ? 'text-brand-green' : 'text-gray-400'}`} />
                    <p className="font-medium text-white">{t.yes}</p>
                    <p className="text-sm text-gray-400 mt-1">{t.configureVpn}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, useVpn: false, vpnType: null, vpnConfig: null }))}
                    className={`p-6 rounded-xl border-2 transition-all ${
                      formData.useVpn === false
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                    }`}
                  >
                    <Globe className={`w-8 h-8 mx-auto mb-3 ${formData.useVpn === false ? 'text-brand-green' : 'text-gray-400'}`} />
                    <p className="font-medium text-white">{t.no}</p>
                    <p className="text-sm text-gray-400 mt-1">{t.externalOnly}</p>
                  </button>
                </div>

                {formData.useVpn && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white">{t.vpnType}</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {['OpenVPN', 'WireGuard', 'L2TP'].map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, vpnType: type }))}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.vpnType === type
                              ? 'border-brand-green bg-brand-green/10'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <FileKey className={`w-6 h-6 mx-auto mb-2 ${formData.vpnType === type ? 'text-brand-green' : 'text-gray-400'}`} />
                          <p className={`text-sm font-medium ${formData.vpnType === type ? 'text-brand-green' : 'text-gray-300'}`}>
                            {type}
                          </p>
                        </button>
                      ))}
                    </div>

                    {formData.vpnType && (
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t.configFile}
                        </label>
                        <div
                          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                            formData.vpnConfig
                              ? 'border-brand-green bg-brand-green/5'
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="file"
                            accept=".ovpn,.conf,.l2tp"
                            onChange={handleVpnFileUpload}
                            className="hidden"
                            id="vpn-file"
                          />
                          {formData.vpnConfig ? (
                            <div className="flex items-center justify-center gap-3">
                              <FileKey className="w-8 h-8 text-brand-green" />
                              <div className="text-left">
                                <p className="text-white font-medium">{formData.vpnConfigName}</p>
                                <p className="text-sm text-gray-400">{t.fileReady}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, vpnConfig: null, vpnConfigName: '' }))}
                                className="ml-4 p-2 hover:bg-gray-700 rounded-lg"
                              >
                                <X className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>
                          ) : (
                            <label htmlFor="vpn-file" className="cursor-pointer">
                              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-300">
                                {t.dragFile} <span className="text-brand-green">{t.browse}</span>
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                .ovpn, .conf ou .l2tp
                              </p>
                            </label>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, useVpn: 'later' }))}
                          className="mt-4 text-sm text-gray-400 hover:text-gray-300"
                        >
                          {t.configureLater}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <HelpGuidePanel step="vpn" t={t} isOpen={showGuides} />
              </div>
            )}

            {/* Step 6: Complete */}
            {currentStep === 6 && (
              <div className="space-y-8">
                <StepHeader
                  icon={<Rocket className="w-7 h-7 text-brand-green" />}
                  title={t.step7Title}
                  description={t.step7Desc}
                  stepNumber={7}
                  totalSteps={steps.length}
                />

                {/* Success Card */}
                <div className="text-center p-10 bg-gradient-to-br from-brand-green/10 via-cyan-500/5 to-purple-500/10 border border-brand-green/30 rounded-2xl">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-brand-green/30 to-cyan-500/30 flex items-center justify-center animate-pulse">
                    <Sparkles className="w-10 h-10 text-brand-green" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">
                    {t.congrats} 🎉
                  </h2>
                  <p className="text-gray-400 max-w-md mx-auto">
                    {t.thankYou}
                  </p>
                </div>

                {/* Launch Button */}
                <div className="text-center">
                  {!loading ? (
                    <button
                      onClick={handleLaunch}
                      className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-brand-green to-cyan-500 text-black font-bold text-lg rounded-xl hover:opacity-90 transition-all transform hover:scale-105 shadow-lg shadow-brand-green/25"
                    >
                      <Play className="w-6 h-6" />
                      {t.launchPlatform}
                    </button>
                  ) : (
                    <div className="space-y-6 p-8 bg-gray-800/30 rounded-2xl border border-gray-700">
                      <div className="flex items-center justify-center gap-3">
                        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
                        <span className="text-lg text-gray-300">{t.preparing}</span>
                      </div>
                      <div className="w-80 mx-auto h-3 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-brand-green to-cyan-500 rounded-full animate-progress" />
                      </div>
                      <p className="text-sm text-gray-500">
                        {t.dockerSetup}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep > 0 && currentStep < 6 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-800">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t.back}
                </button>

                <button
                  onClick={nextStep}
                  disabled={
                    loading ||
                    (currentStep === 2 && !isPasswordValid) ||
                    (currentStep === 4 && (!formData.contractAccepted || !formData.contractOtpVerified || !formData.contractSignature))
                  }
                  className="flex items-center gap-2 px-6 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.saving}
                    </>
                  ) : (
                    <>
                      {t.continue}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resume Progress Modal */}
      {showResumeModal && savedProgress && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t.resumeProgress}</h3>
                <p className="text-sm text-gray-400">
                  Step {savedProgress.currentStep + 1}/{steps.length}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleResumeProgress(false)}
                className="flex-1 px-4 py-3 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
              >
                {t.resumeNo}
              </button>
              <button
                onClick={() => handleResumeProgress(true)}
                className="flex-1 px-4 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-colors"
              >
                {t.resumeYes}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Fullscreen Modal */}
      {contractFullscreen && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex flex-col z-50">
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-brand-green" />
              <span className="font-medium text-white">{t.contractTitle}</span>
            </div>
            <button
              onClick={() => setContractFullscreen(false)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Minimize2 className="w-4 h-4" />
              {t.closeFullscreen}
            </button>
          </div>
          <div
            className="flex-1 overflow-y-auto p-6 md:p-12"
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollTop + clientHeight >= scrollHeight - 20) {
                setFormData(prev => ({ ...prev, contractScrolledToBottom: true }));
              }
            }}
          >
            <div className="max-w-3xl mx-auto prose prose-invert prose-lg">
              <h1 className="text-2xl font-bold text-white mb-6">CONTRAT D'UTILISATION DE LA PLATEFORME SYLINK PENTEST</h1>
              <h2 className="text-xl font-semibold text-brand-green mb-4">CONDITIONS GÉNÉRALES D'UTILISATION ET LIMITATION DE RESPONSABILITÉ</h2>

              <div className="bg-gray-800/50 p-4 rounded-lg mb-6">
                <p className="text-gray-400 m-0">
                  <strong>SYLINK TECHNOLOGIE SAS</strong><br />
                  SIREN : 829 104 595 | SIRET : 829 104 595 00022<br />
                  Capital social : 217 065,00 € | RCS Clermont-Ferrand<br />
                  35 Rue Blatin, 63000 Clermont-Ferrand, France
                </p>
              </div>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">PRÉAMBULE</h3>
              <p>Le présent contrat définit les conditions d'utilisation de la plateforme SYLink Pentest, éditée et opérée par SYLink Technologie SAS. La Plateforme est une solution professionnelle d'automatisation des tests d'intrusion destinée exclusivement aux professionnels de la cybersécurité.</p>

              <p className="text-amber-300 font-semibold my-4 p-3 bg-amber-500/10 rounded-lg">L'acceptation du présent Contrat est un préalable obligatoire à toute utilisation de la Plateforme.</p>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">PARTIE I - CADRE LÉGAL</h3>
              <p><strong>Article 323-1 du Code Pénal :</strong> Le fait d'accéder ou de se maintenir, frauduleusement, dans tout ou partie d'un système de traitement automatisé de données est puni de trois ans d'emprisonnement et de 100 000 euros d'amende.</p>
              <p><strong>Article 323-2 du Code Pénal :</strong> Le fait d'entraver ou de fausser le fonctionnement d'un système de traitement automatisé de données est puni de cinq ans d'emprisonnement et de 150 000 euros d'amende.</p>

              <p className="text-red-400 font-semibold my-4 p-3 bg-red-500/10 rounded-lg">AVERTISSEMENT : La réalisation de tests d'intrusion sur un système informatique sans autorisation explicite constitue une infraction pénale.</p>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">PARTIE II - RESPONSABILITÉS</h3>
              <p><strong>L'UTILISATEUR EST SEUL ET UNIQUE RESPONSABLE</strong> de l'utilisation qu'il fait de la Plateforme SYLink Pentest et des actions qu'il entreprend au moyen de celle-ci.</p>
              <p>En utilisant la Plateforme, l'Utilisateur garantit :</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Disposer de toutes les autorisations légales nécessaires</li>
                <li>Respecter scrupuleusement le périmètre défini dans les autorisations</li>
                <li>Ne pas utiliser la Plateforme à des fins illicites ou malveillantes</li>
                <li>Disposer des compétences techniques nécessaires</li>
              </ul>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">PARTIE III - LIMITATION DE RESPONSABILITÉ</h3>
              <p><strong>SYLINK TECHNOLOGIE SAS DÉCLINE TOUTE RESPONSABILITÉ</strong> quant aux actions entreprises par l'Utilisateur au moyen de la Plateforme.</p>
              <p>SYLink Technologie SAS ne saurait être tenue responsable des dommages directs ou indirects résultant de l'utilisation de la Plateforme.</p>

              <h3 className="text-lg font-semibold text-white mt-6 mb-3">ACCEPTATION DU CONTRAT</h3>
              <p>En signant ce contrat, l'Utilisateur reconnaît :</p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Avoir lu et compris l'intégralité du présent Contrat</li>
                <li>Accepter sans réserve l'ensemble de ses dispositions</li>
                <li>Être informé du cadre légal applicable aux tests d'intrusion</li>
                <li>Comprendre que l'obtention d'autorisations préalables est obligatoire</li>
                <li>Assumer l'entière responsabilité de l'utilisation de la Plateforme</li>
                <li>Dégager SYLink Technologie SAS de toute responsabilité</li>
              </ol>

              <div className="mt-8 p-4 bg-gray-800/50 rounded-lg">
                <p className="text-sm text-gray-400 m-0">
                  <strong>Documents complémentaires :</strong><br />
                  • <a href="https://sylink-pentest.com/cgv" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">Conditions générales de vente (CGV)</a><br />
                  • <a href="https://sylink-pentest.com/cgu" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">Conditions générales d'utilisation (CGU)</a>
                </p>
              </div>

              <p className="text-gray-500 mt-8 text-sm text-center">
                Document contractuel - Tous droits réservés - SYLink Technologie SAS © 2017-2026<br />
                Date de dernière mise à jour : Janvier 2026
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save Progress Toast */}
      {showSaveToast && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 shadow-lg flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-green/20 flex items-center justify-center">
              <Check className="w-4 h-4 text-brand-green" />
            </div>
            <span className="text-sm text-gray-300">{t.progressSaved}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupWizard;
